<template>
  <div>
    <base-title
      :title="StringLanguage.deliveryRegions"
      :subtitle="
        onBoardDone ? StringLanguage.configurations : StringLanguage.back
      "
      :link="onBoardDone ? 'configuracoes' : 'onboard'"
    >
      <base-button
        v-if="regions.length > 0"
        :text="StringLanguage.addRegion"
        class="m_auto w-100"
        :class="{ reverse: !!isMobile }"
        @click="add()"
      />
    </base-title>

    <base-empty
      v-if="regions.length == 0"
      title="Você ainda não cadastrou regiões de entrega."
      message="Suas regiões de entrega irão aparecer por aqui."
      buttonText="Adicionar região de entrega"
      @onClick="add()"
    />

    <base-box
      class="region"
      v-for="(radius, i) in regions"
      :key="radius.radius"
    >
      <div class="kms">
        <p>{{ `Região ${i + 1}` }}</p>
        <div class="radius mb_10">
          <p>{{ StringLanguage.from }}</p>
          <p>
            {{
              Utils.convertMoney(
                regions[i - 1] ? regions[i - 1].up_to || 0 : 0,
                '',
                'km'
              )
            }}
          </p>
        </div>
        <div class="radius">
          <p>{{ StringLanguage.to }}</p>
          <p>
            <span>{{ Utils.convertMoney(radius.up_to, '', 'km') }}</span>
          </p>
          <div
            class="minus-plus"
            :class="{ disabled: i !== regions.length - 1 }"
            v-if="i === regions.length - 1"
          >
            <div
              @mousedown="start($event, i, 'decrease')"
              @mouseleave="stop"
              @mouseup="stop"
              @touchstart="start($event, i, 'decrease')"
              @touchend="stop"
              @touchcancel="stop"
              @click="changeRadius($event, i, 'decrease')"
            >
              <span
                class="minus"
                :class="`${
                  radius.up_to >=
                    (
                      (regions[i - 1] ? +regions[i - 1].up_to || 0 : 0) + 0.2
                    ).toFixed(2) && i === regions.length - 1
                    ? ''
                    : 'disabled'
                }`"
              >
                <i class="fal fa-minus"></i>
              </span>
            </div>
            <div
              @mousedown="start($event, i, 'increase')"
              @mouseleave="stop"
              @mouseup="stop"
              @touchstart="start($event, i, 'increase')"
              @touchend="stop($event, i, 'increase')"
              @touchcancel="stop"
              @click="changeRadius($event, i, 'increase')"
            >
              <span
                class="plus"
                :class="{ disabled: i !== regions.length - 1 }"
              >
                <i class="fal fa-plus"></i>
              </span>
            </div>
          </div>
        </div>
        <base-button
          class="remove-region"
          title="Remover região"
          v-if="i == regions.length - 1 && regions.length > 1"
          @click="pop()"
          design="four"
        >
          <i class="fal fa-trash"></i>
        </base-button>
      </div>
      <div class="row w-100">
        <div class="col-lg-6 values pr_20">
          <div class="d-flex">
            <base-input
              design="two"
              :key="!!radius.free_fee"
              :label="
                !!radius.free_fee
                  ? `${StringLanguage.value} (${Utils.convertMoney(
                      radius.old_price
                    )})`
                  : StringLanguage.value
              "
              v-model.lazy="radius.price"
              v-bind="money"
              :money-mask="StringLanguage.currencyConfigs"
              :disabled="deleteMode || loadingData || radius.free_fee"
              :masked="false"
              @paste.prevent=""
              @keydown="
                (e) => {
                  validNegative(e);
                }
              "
              @blur="
                (e) => {
                  blockNegative(e, regions[i], 'price');
                }
              "
              class="input-fee w-100"
            />
            <div class="w-100" :class="!isMobile && 'pt_10'">
              <base-checkbox
                class="free-fee"
                v-model="radius.free_fee"
                :checked="radius.free_fee"
                @click="setFreeFee(i)"
              >
                <span class="text-ff">Taxa grátis</span>
              </base-checkbox>
            </div>
          </div>
          <base-input
            class="mb_0"
            :label="StringLanguage.minOrder"
            v-model.lazy="radius.min_order"
            v-bind="money"
            :disabled="deleteMode || loadingData"
            :money-mask="StringLanguage.currencyConfigs"
            :masked="false"
            @paste.prevent=""
            @keydown="
              (e) => {
                validNegative(e);
              }
            "
            @blur="
              (e) => {
                blockNegative(e, regions[i], 'min_order');
              }
            "
          />
        </div>
        <div class="col-lg-6 times pl_20">
          <base-input
            :label="StringLanguage.minDeliveryTime"
            v-model="radius.minDelivery"
            :disabled="!!allowTime"
            :moneyMask="StringLanguage.timeConfigs.minutes"
            :max="8"
            :valid="userError !== `minDelivery${i}`"
            @focus="clearError(`minDelivery${i}`)"
          />
          <base-input
            class="mb_5"
            :label="StringLanguage.maxDeliveryTime"
            v-model="radius.maxDelivery"
            :disabled="!!allowTime"
            :moneyMask="StringLanguage.timeConfigs.minutes"
            :max="8"
            :valid="userError !== `maxDelivery${i}`"
            @focus="clearError(`maxDelivery${i}`)"
          />
        </div>
      </div>
    </base-box>

    <base-add
      :text="StringLanguage.addRegion"
      @onClick="add()"
      height="170px"
    />

    <div
      v-if="regions.length > 0 && hasChanges()"
      class="d-flex justify-end w-100"
    >
      <base-button design="save" @click="onSave" />
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import { TheMask } from 'vue-the-mask';
import { Money } from 'v-money';
import DeliveryRegion from '@/plugins/core/schema/configurations/DeliveryRegion/DeliveryRegion';
import StoreAPI from '@/api/store';
import Vue from 'vue';
import Utils from '@/utils';

import BaseTitle from '@/components/Title';
import BaseButton from '@/components/Button';
import BaseBox from '@/components/Box';
import BaseInput from '@/components/Input';
import BaseCheckbox from '@/components/Checkbox';
import BaseLabel from '@/components/Label';
import BaseEmpty from '@/components/Empty';
import BaseAdd from '@/components/Add';

export default {
  data() {
    return {
      allowTime: false,
      Utils,
      deleteMode: false,
      loadingData: false,
      money: {
        decimal: ',',
        thousands: '.',
        precision: 2,
        masked: false,
      },
      km: {
        decimal: ',',
        precision: 1,
        suffix: ' km',
        masked: false,
      },
      regions: new DeliveryRegion([DeliveryRegion.getTemplateRange()]).ranges,
      freeFee: false,
      originalData: null,
      moneyMask: StringLanguage.currencyConfigs,
      interval: false,
      userError: null,
    };
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasChanges()) {
      confirm(`${StringLanguage.confirmComplement}`, (res) => {
        if (res) next();
      });
    } else {
      next();
    }
  },
  created() {
    let self = this;
    this.loadingData = true;
    StoreAPI.storeGetConfiguration('delivery_region')
      .then((response) => {
        self.set(response);
      })
      .catch((err) => {
        alert(StringLanguage.loadError);
      })
      .finally(() => {
        this.loadingData = false;

        if (this.regions && this.regions.length && this.regions[0].up_to == 0) {
          alert(StringLanguage.radiusError);
        }

        if (this.regions && !this.regions.length) {
          this.regions.push(DeliveryRegion.getTemplateRange(0.1));
        }
      });
  },
  methods: {
    hasChanges() {
      return JSON.stringify(this.originalData) != JSON.stringify(this.regions);
    },
    onSave() {
      if (this.hasChanges()) {
        if (this.regions.length) {
          for (let i = 0; i < this.regions.length; i++) {
            let region = this.regions[i];
            if (i > 0) {
              // se não preencheu nada, somente remove do array e salva
              if (
                +region.up_to == 0 &&
                +region.min_order == 0 &&
                !region.free_fee &&
                +region.price == 0 &&
                +region.minDelivery == 0 &&
                +region.maxDelivery == 0
              ) {
                this.regions.splice(i, 1);
                continue;
              }

              // se preencheu, e foi um valor abaixo do anterior exibe erro
              if (+this.regions[i].up_to <= +this.regions[i - 1].up_to) {
                alert(StringLanguage.radiusError);
                return false;
              }
            }

            //validação de tempos de entrega em indices maiores que o primeiro
            if (!this.validateTimes(region, i)) {
              return;
            }
          }
        }

        this.save();
      } else {
        alert(StringLanguage.noChanges);
      }
    },
    set(response) {
      if (response.getErrors([]).length > 0) {
        return false;
      } else if (
        response.getData({ delivery_region: [] }).delivery_region ||
        false
      ) {
        Vue.set(
          this,
          'regions',
          new DeliveryRegion(response.getData([]).delivery_region || []).ranges
        );
        Vue.set(
          this,
          'originalData',
          new DeliveryRegion(response.getData([]).delivery_region || []).ranges
        );
      } else {
        Vue.set(
          this,
          'regions',
          new DeliveryRegion([DeliveryRegion.getTemplateRange(0)]).ranges
        );
        Vue.set(
          this,
          'originalData',
          new DeliveryRegion([DeliveryRegion.getTemplateRange(0)]).ranges
        );
      }
      this.regions.sort((a, b) => a.up_to - b.up_to);
      this.$forceUpdate();
    },
    add() {
      if (this.regions && this.regions.length == 1) {
        if (this.regions[0].up_to == 0) {
          alert(StringLanguage.radiusError);
          return;
        }
      }

      //identifica valor da região anterior e adiciona 100m
      const newRegion = parseFloat(
        (+this.regions[this.regions.length - 1].up_to + 0.1).toFixed(2)
      );
      this.regions.push(DeliveryRegion.getTemplateRange(newRegion));
    },
    pop() {
      confirm('Tem certeza que deseja remover esta área de entrega?', (res) => {
        if (res) {
          this.regions.pop();
        }
      });
    },
    save() {
      let self = this;
      this.loadingData = true;
      return StoreAPI.storeConfiguration({
        delivery_region: this.regions,
      })
        .then((response) => {
          this.$store.dispatch('store/getConfigurations');
          self.set(response);
          if (!this.onBoardDone) {
            return this.$router.push({ name: 'onboard' });
          }

          alert(StringLanguage.saveSuccess);
        })
        .catch((err) => {
          alert(StringLanguage.saveError);
        })
        .finally(() => {
          this.loadingData = false;
        });
    },
    validateTimes(region, i) {
      if (
        this.validTime(region.minDelivery, `minDelivery${i}`) ||
        this.validTime(region.maxDelivery, `maxDelivery${i}`)
      ) {
        alert(StringLanguage.errorTimeInvalid);
        return false;
      }

      if (+region.maxDelivery <= +region.minDelivery) {
        this.userError = `maxDelivery${i}`;
        alert(StringLanguage.errorMaxMinDeliveryTime);
        return false;
      }
      return true;
    },
    validTime(value, prop) {
      if (+value <= 0) {
        this.userError = prop;
        return true;
      }
      return false;
    },
    isValid(beforeRegion, region) {
      //se o início da atual for menor ou igual ao fim da anterior, erro
      if (beforeRegion && +beforeRegion.up_to <= +region.from) {
        alert(StringLanguage.radiusError);
        region.from = beforeRegion.up_to + 1;
      }

      if (beforeRegion && +beforeRegion.up_to > +region.up_to) {
        alert(StringLanguage.radiusError);
        region.up_to = beforeRegion.up_to;
      }
    },
    clearError(prop) {
      if (this.userError === prop) this.userError = null;
    },
    validNegative(event) {
      return event.key === '-' ? event.preventDefault() : null;
    },
    blockNegative(event, vModel, key) {
      if (/-/.test(event.target.value)) {
        vModel[key] = event.target.value.replace('-', '');
        this.$forceUpdate();
      }
    },
    setFreeFee(index) {
      if (!!!this.regions[index].free_fee) {
        this.regions[index].old_price = this.regions[index].price;
        this.regions[index].price = '';
      } else {
        this.regions[index].price = this.regions[index].old_price;
        this.regions[index].old_price = '0';
      }
    },
    start(event, index, option = 'increase') {
      event.preventDefault();

      const value = option === 'increase' ? 0.1 : -0.1;

      if (!this.interval) {
        this.interval = setInterval(() => {
          if (index === 0 && +this.regions[index].up_to + value < 0.1)
            return false;

          if (
            index >= 1 &&
            +this.regions[index].up_to + value <= +this.regions[index - 1].up_to
          ) {
            return false;
          }

          this.regions[index].up_to = parseFloat(
            (+this.regions[index].up_to + value).toFixed(2)
          );
        }, 150);
      }
    },
    stop(event) {
      event.preventDefault();
      clearInterval(this.interval);
      this.interval = false;
    },
    changeRadius(event, index, option = 'increase') {
      event.preventDefault();
      const value = option === 'increase' ? 0.1 : -0.1;

      if (index === 0 && +this.regions[index].up_to + value < 0.1) return false;

      if (
        index >= 1 &&
        +this.regions[index].up_to + value <= +this.regions[index - 1].up_to
      ) {
        return false;
      }

      this.regions[index].up_to = parseFloat(
        (+this.regions[index].up_to + value).toFixed(2)
      );
    },
  },
  computed: {
    ...mapState({
      isMobile: (state) => state.store.isMobile,
      onBoardDone: (state) => state.store.onBoardDone,
    }),
  },
  components: {
    TheMask,
    Money,
    BaseTitle,
    BaseButton,
    BaseBox,
    BaseInput,
    BaseCheckbox,
    BaseLabel,
    BaseEmpty,
    BaseAdd,
  },
};
</script>
<style scoped>
.box-regions {
  padding-top: 20px;
  padding-bottom: 10px;
}
.free-fee {
  width: 100%;
  padding: 14.5px 15px;
  background: var(--box-up);
  border: 1px solid var(--box-up);
  border-radius: 0 5px 5px 0;
  height: 45px;
}
.text-ff {
  color: var(--label-primary);
  font-size: 12px;
  white-space: nowrap;
}
.remove {
  cursor: pointer;
  margin-bottom: 10px;
  color: var(--default);
  font-weight: 300;
  font-size: 1.3em;
  text-transform: uppercase;
}

.region {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.kms {
  background: var(--box-up);
  padding: 21px 20px 23px;
  font-weight: 900;
  color: #999;
  width: 230px;
  border-radius: 5px;
  margin-right: 15px;
  position: relative;
  min-height: 122px;
}

.kms > p {
  font-size: 1.6em;
  margin-bottom: 20px;
  color: var(--default);
  font-weight: bold;
}

.minus-plus {
  display: flex;
  position: absolute;
  right: 15px;
}

.minus-plus.disabled {
  pointer-events: none;
}

.minus-plus > div {
  padding: 6px;
  cursor: pointer;
}

.minus,
.plus {
  border-radius: 50%;
  width: 18px;
  height: 18px;
  text-align: center;
  background-color: #8c8c8c57;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.minus i,
.plus i {
  font-size: 9px;
  color: var(--label-primary);
}

.minus.disabled,
.plus.disabled {
  opacity: 0.7;
}

.radius {
  display: flex;
  align-items: center;
}

.radius p:first-child {
  font-weight: 400;
  min-width: 25px;
}

.radius p:nth-child(2) {
  font-size: 1.4em;
  font-weight: 900;
  margin-right: 25px;
  display: flex;
  align-items: center;
  color: var(--label-secondary);
}

.radius p:nth-child(2) > span:first-child {
  min-width: 68px;
}

.values {
  border-right: 1px solid #ffffff1f;
}

.remove-region {
  padding: 10px 12px !important;
  position: absolute;
  top: 15px;
  right: 15px;
  min-width: auto;
}

.input-fee input {
  border-radius: 5px 0 0 5px !important;
}

@media (max-width: 991px) {
  /* Mobile */
  .free-fee {
    margin: 10px 0 5px 0;
    padding: 15px;
    width: 100%;
  }

  .minus-plus > div {
    padding: 20px 6px;
  }

  .minus-plus > div:first-child {
    padding-left: 20px;
  }

  .minus-plus > div:last-child {
    padding-right: 20px;
  }

  .region {
    display: block;
  }

  .kms {
    width: 100%;
    margin: 0 0 20px 0;
    min-height: auto;
  }

  .kms + div {
    width: auto !important;
  }

  .values,
  .times {
    padding: 0 15px !important;
    border: none;
  }

  .values {
    margin-bottom: 18px;
  }
}
</style>
